// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * All DOM rendering for zipsafe: the mode switch, the shared drop zone, the
 * progress bar, the extract tree, the create queue, result cards, modals, and
 * the status bar. main.ts owns the domain state and passes in handlers; this
 * module never touches the worker or the network directly.
 */

import type { ExtractedFile, InputFile, Mode, TreeNode, ZipEntry } from './types';
import { buildTree, countFiles } from './paths';
import { formatBytes, formatRatio } from './format';
import { supportsDirectoryPicker, supportsSavePicker } from './fsaccess';

export interface UIHandlers {
  onFiles(files: File[]): void;
  onSample(): void;
  onModeChange(mode: Mode): void;
  onExtract(paths: string[] | null): void;
  onSaveExtractedToFolder(): void;
  onDownloadExtracted(path: string): void;
  onCreate(level: number): void;
  onSaveArchive(): void;
  onShareArchive(): void;
  onReset(): void;
}

export interface UI {
  setMode(mode: Mode): void;
  showTree(entries: ZipEntry[], sourceName: string): void;
  showExtracted(files: ExtractedFile[]): void;
  showCreateQueue(files: InputFile[]): void;
  showCreated(size: number, originalSize: number, name: string, canShare: boolean): void;
  setProgress(done: number, total: number, label: string): void;
  hideProgress(): void;
  showError(message: string): void;
  setStatus(label: string, dot: 'idle' | 'busy' | 'ok' | 'err'): void;
  setThroughput(text: string): void;
  reset(): void;
}

const ACCEPT_HINTS: Record<Mode, string> = {
  extract: 'Drop a .zip here, or click to choose one',
  create: 'Drop files here, or click to choose — they become one .zip',
};

export function createUI(app: HTMLElement, handlers: UIHandlers): UI {
  let mode: Mode = 'extract';

  app.innerHTML = `
    <div class="workspace">
      <div class="mode-switch" role="tablist" aria-label="Choose action">
        <button type="button" role="tab" class="mode-btn active" data-mode="extract">
          <span class="mode-emoji" aria-hidden="true">📂</span> Extract a ZIP
        </button>
        <button type="button" role="tab" class="mode-btn" data-mode="create">
          <span class="mode-emoji" aria-hidden="true">🗜️</span> Create a ZIP
        </button>
      </div>

      <div class="dropzone" id="dropzone" tabindex="0" role="button"
           aria-label="Drop files or choose files">
        <input type="file" id="file-input" hidden />
        <div class="dz-inner">
          <div class="dz-icon" aria-hidden="true">⬆</div>
          <p class="dz-title" id="dz-title">${ACCEPT_HINTS.extract}</p>
          <p class="dz-sub">Files are processed in your browser and never uploaded.</p>
          <button type="button" class="sample-link" id="sample-btn">
            <svg class="ic" viewBox="0 0 24 24" width="14" height="14" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round"
                 stroke-linejoin="round" aria-hidden="true">
              <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
            </svg>
            Try a sample .zip
          </button>
        </div>
      </div>

      <div class="progress-wrap" id="progress-wrap" hidden aria-live="polite">
        <div class="progress-row">
          <span class="progress-label" id="progress-label">Working…</span>
          <span class="progress-pct" id="progress-pct">0%</span>
        </div>
        <div class="progress-track"><div class="progress-bar" id="progress-bar"></div></div>
      </div>

      <div class="error-banner" id="error-banner" hidden role="alert">
        <span class="err-msg" id="err-msg"></span>
        <button type="button" class="btn-ghost" id="err-dismiss">dismiss</button>
      </div>

      <div class="result" id="result"></div>
    </div>`;

  const dropzone = app.querySelector<HTMLElement>('#dropzone')!;
  const fileInput = app.querySelector<HTMLInputElement>('#file-input')!;
  const sampleBtn = app.querySelector<HTMLButtonElement>('#sample-btn')!;
  const dzTitle = app.querySelector<HTMLElement>('#dz-title')!;
  const result = app.querySelector<HTMLElement>('#result')!;
  const progressWrap = app.querySelector<HTMLElement>('#progress-wrap')!;
  const progressBar = app.querySelector<HTMLElement>('#progress-bar')!;
  const progressLabel = app.querySelector<HTMLElement>('#progress-label')!;
  const progressPct = app.querySelector<HTMLElement>('#progress-pct')!;
  const errorBanner = app.querySelector<HTMLElement>('#error-banner')!;
  const errMsg = app.querySelector<HTMLElement>('#err-msg')!;

  const sbLabel = document.querySelector<HTMLElement>('#sb-status-label')!;
  const sbDot = document.querySelector<HTMLElement>('#sb-status-dot')!;
  const sbDetail = document.querySelector<HTMLElement>('#sb-detail')!;
  const sbThroughput = document.querySelector<HTMLElement>('#sb-throughput')!;

  /* ── mode switch ── */
  app.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.mode as Mode;
      if (next === mode) return;
      handlers.onModeChange(next);
    });
  });

  /* ── sample ── */
  sampleBtn.addEventListener('click', (e) => {
    // The pill sits inside the dropzone, whose click opens the file picker —
    // stop the event so we don't also trigger that.
    e.stopPropagation();
    handlers.onSample();
  });

  /* ── drop zone ── */
  dropzone.addEventListener('click', () => fileInput.click());
  dropzone.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      fileInput.click();
    }
  });
  fileInput.addEventListener('change', () => {
    if (fileInput.files && fileInput.files.length) {
      handlers.onFiles(Array.from(fileInput.files));
      fileInput.value = '';
    }
  });
  ['dragenter', 'dragover'].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add('dragover');
    }),
  );
  ['dragleave', 'dragend'].forEach((evt) =>
    dropzone.addEventListener(evt, () => dropzone.classList.remove('dragover')),
  );
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    const dt = (e as DragEvent).dataTransfer;
    if (dt && dt.files.length) handlers.onFiles(Array.from(dt.files));
  });

  app.querySelector('#err-dismiss')!.addEventListener('click', () => {
    errorBanner.hidden = true;
  });

  /* ── render helpers ── */

  function renderTreeRows(node: TreeNode, depth: number, into: HTMLElement): void {
    for (const child of node.children) {
      const row = document.createElement('div');
      row.className = child.isDir ? 'tree-row dir' : 'tree-row file';
      row.style.paddingLeft = `${depth * 16 + 12}px`;

      if (child.isDir) {
        row.innerHTML = `
          <span class="tree-icon" aria-hidden="true">📁</span>
          <span class="tree-name">${escapeHtml(child.name)}</span>
          <span class="tree-size">${formatBytes(child.size)}</span>`;
        into.appendChild(row);
        renderTreeRows(child, depth + 1, into);
      } else {
        row.innerHTML = `
          <input type="checkbox" class="tree-check" data-path="${escapeAttr(child.path)}" checked />
          <span class="tree-icon" aria-hidden="true">📄</span>
          <span class="tree-name">${escapeHtml(child.name)}</span>
          <span class="tree-size">${formatBytes(child.size)}</span>`;
        into.appendChild(row);
      }
    }
  }

  return {
    setMode(next) {
      mode = next;
      app.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((b) =>
        b.classList.toggle('active', b.dataset.mode === next),
      );
      dzTitle.textContent = ACCEPT_HINTS[next];
      // The sample is a .zip to open, so only offer it in the extract flow.
      sampleBtn.hidden = next !== 'extract';
      fileInput.accept = next === 'extract' ? '.zip,application/zip' : '';
      fileInput.multiple = next === 'create';
      result.innerHTML = '';
      errorBanner.hidden = true;
      this.hideProgress();
    },

    showTree(entries, sourceName) {
      const tree = buildTree(entries);
      const fileCount = countFiles(tree);
      const totalSize = tree.size;

      const card = document.createElement('div');
      card.className = 'card';
      card.innerHTML = `
        <div class="card-head">
          <div>
            <h2 class="card-title">${escapeHtml(sourceName)}</h2>
            <p class="card-sub">${fileCount} file${fileCount === 1 ? '' : 's'} · ${formatBytes(totalSize)} uncompressed</p>
          </div>
          <button type="button" class="btn-ghost" data-act="reset">choose another</button>
        </div>
        <div class="tree" id="tree"></div>
        <div class="card-actions">
          <label class="select-all"><input type="checkbox" id="select-all" checked /> select all</label>
          <div class="spacer"></div>
          <button type="button" class="btn-secondary" data-act="extract-selected">Extract selected</button>
          <button type="button" class="btn-primary" data-act="extract-all">Extract all</button>
        </div>`;
      result.innerHTML = '';
      result.appendChild(card);

      renderTreeRows(tree, 0, card.querySelector<HTMLElement>('#tree')!);

      const checks = () => Array.from(card.querySelectorAll<HTMLInputElement>('.tree-check'));
      const selectAll = card.querySelector<HTMLInputElement>('#select-all')!;
      selectAll.addEventListener('change', () => {
        checks().forEach((c) => (c.checked = selectAll.checked));
      });

      card.querySelector('[data-act="reset"]')!.addEventListener('click', () => handlers.onReset());
      card.querySelector('[data-act="extract-all"]')!.addEventListener('click', () =>
        handlers.onExtract(null),
      );
      card.querySelector('[data-act="extract-selected"]')!.addEventListener('click', () => {
        const paths = checks().filter((c) => c.checked).map((c) => c.dataset.path!);
        if (paths.length) handlers.onExtract(paths);
      });
    },

    showExtracted(files) {
      const totalSize = files.reduce((n, f) => n + f.data.length, 0);
      const card = document.createElement('div');
      card.className = 'card';
      const folderBtn = supportsDirectoryPicker()
        ? `<button type="button" class="btn-primary" data-act="save-folder">Save all to folder…</button>`
        : `<span class="hint">Tip: download each file below — your browser doesn't support folder saving.</span>`;
      card.innerHTML = `
        <div class="card-head">
          <div>
            <h2 class="card-title">Extracted ${files.length} file${files.length === 1 ? '' : 's'}</h2>
            <p class="card-sub">${formatBytes(totalSize)} written to memory — save below.</p>
          </div>
          <button type="button" class="btn-ghost" data-act="reset">done</button>
        </div>
        <div class="file-list" id="extracted-list"></div>
        <div class="card-actions">
          <div class="spacer"></div>
          ${folderBtn}
        </div>`;
      result.innerHTML = '';
      result.appendChild(card);

      const list = card.querySelector<HTMLElement>('#extracted-list')!;
      for (const f of files) {
        const row = document.createElement('div');
        row.className = 'file-row';
        row.innerHTML = `
          <span class="tree-icon" aria-hidden="true">📄</span>
          <span class="tree-name">${escapeHtml(f.path)}</span>
          <span class="tree-size">${formatBytes(f.data.length)}</span>
          <button type="button" class="btn-mini" data-path="${escapeAttr(f.path)}">download</button>`;
        list.appendChild(row);
      }
      list.querySelectorAll<HTMLButtonElement>('.btn-mini').forEach((b) =>
        b.addEventListener('click', () => handlers.onDownloadExtracted(b.dataset.path!)),
      );
      const folder = card.querySelector('[data-act="save-folder"]');
      if (folder) folder.addEventListener('click', () => handlers.onSaveExtractedToFolder());
      card.querySelector('[data-act="reset"]')!.addEventListener('click', () => handlers.onReset());
    },

    showCreateQueue(files) {
      const totalSize = files.reduce((n, f) => n + f.size, 0);
      const card = document.createElement('div');
      card.className = 'card';
      card.innerHTML = `
        <div class="card-head">
          <div>
            <h2 class="card-title">${files.length} file${files.length === 1 ? '' : 's'} ready to zip</h2>
            <p class="card-sub">${formatBytes(totalSize)} total</p>
          </div>
          <button type="button" class="btn-ghost" data-act="reset">clear</button>
        </div>
        <div class="file-list" id="queue-list"></div>
        <div class="level-row">
          <label for="level">Compression</label>
          <input type="range" id="level" min="0" max="9" value="6" />
          <span class="level-val" id="level-val">balanced (6)</span>
        </div>
        <div class="card-actions">
          <div class="spacer"></div>
          <button type="button" class="btn-primary" data-act="create">Create ZIP</button>
        </div>`;
      result.innerHTML = '';
      result.appendChild(card);

      const list = card.querySelector<HTMLElement>('#queue-list')!;
      for (const f of files) {
        const row = document.createElement('div');
        row.className = 'file-row';
        row.innerHTML = `
          <span class="tree-icon" aria-hidden="true">📄</span>
          <span class="tree-name">${escapeHtml(f.name)}</span>
          <span class="tree-size">${formatBytes(f.size)}</span>`;
        list.appendChild(row);
      }

      const level = card.querySelector<HTMLInputElement>('#level')!;
      const levelVal = card.querySelector<HTMLElement>('#level-val')!;
      const describe = (v: number) =>
        v === 0 ? 'store only (0)' : v <= 3 ? `fast (${v})` : v <= 6 ? `balanced (${v})` : `smallest (${v})`;
      level.addEventListener('input', () => (levelVal.textContent = describe(Number(level.value))));

      card.querySelector('[data-act="create"]')!.addEventListener('click', () =>
        handlers.onCreate(Number(level.value)),
      );
      card.querySelector('[data-act="reset"]')!.addEventListener('click', () => handlers.onReset());
    },

    showCreated(size, originalSize, name, canShare) {
      const card = document.createElement('div');
      card.className = 'card success';
      const shareBtn = canShare
        ? `<button type="button" class="btn-secondary" data-act="share">Share…</button>`
        : '';
      card.innerHTML = `
        <div class="card-head">
          <div>
            <h2 class="card-title">${escapeHtml(name)}</h2>
            <p class="card-sub">${formatBytes(size)} · ${formatRatio(originalSize, size)} smaller than the originals</p>
          </div>
          <button type="button" class="btn-ghost" data-act="reset">make another</button>
        </div>
        <div class="card-actions">
          <div class="spacer"></div>
          ${shareBtn}
          <button type="button" class="btn-primary" data-act="save">
            ${supportsSavePicker() ? 'Save .zip…' : 'Download .zip'}
          </button>
        </div>`;
      result.innerHTML = '';
      result.appendChild(card);
      card.querySelector('[data-act="save"]')!.addEventListener('click', () => handlers.onSaveArchive());
      const share = card.querySelector('[data-act="share"]');
      if (share) share.addEventListener('click', () => handlers.onShareArchive());
      card.querySelector('[data-act="reset"]')!.addEventListener('click', () => handlers.onReset());
    },

    setProgress(done, total, label) {
      progressWrap.hidden = false;
      const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
      progressBar.style.width = `${pct}%`;
      progressPct.textContent = `${pct}%`;
      progressLabel.textContent = label;
    },

    hideProgress() {
      progressWrap.hidden = true;
      progressBar.style.width = '0%';
    },

    showError(message) {
      errMsg.textContent = message;
      errorBanner.hidden = false;
      this.setStatus('error', 'err');
    },

    setStatus(label, dot) {
      sbLabel.textContent = label;
      sbDot.className = `dot-mini ${dot}`;
    },

    setThroughput(text) {
      sbThroughput.innerHTML = `<span class="sb-key">throughput</span> ${escapeHtml(text)}`;
    },

    reset() {
      result.innerHTML = '';
      errorBanner.hidden = true;
      sbDetail.textContent = '';
      this.hideProgress();
      this.setStatus('ready', 'idle');
      this.setThroughput('—');
    },
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}
